import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import '../styles/NewsLetterDateRangePicker.css';
import { Calendar } from 'lucide-react';

const PRESETS = [
  { label: 'This Month', type: 'this_month' },
  { label: 'Last 30 Days', type: 'last_30_days' },
  { label: 'Past 2 Months', type: 'past_2_months' },
  { label: 'Past 3 Months', type: 'past_3_months' },
  { label: 'Past 6 Months', type: 'past_6_months' },
  { label: 'Past 1 Year', type: 'past_1_year' },
  { label: 'Custom', type: 'custom' }
];

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const formatDateToInput = (dateObj) => {
  if (!dateObj) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateToDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [yyyy, mm, dd] = dateStr.split('-');
  return `${dd}/${mm}/${yyyy}`;
};

const parseDateFromInput = (dateStr) => {
  if (!dateStr) return null;
  const [yyyy, mm, dd] = dateStr.split('-');
  return new Date(yyyy, mm - 1, dd);
};

const parseDisplayToDate = (displayStr) => {
  if (!displayStr) return null;
  const parts = displayStr.split('/');
  if (parts.length === 3) {
    const dd = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    const yyyy = parseInt(parts[2], 10);
    if (!isNaN(mm) && !isNaN(dd) && !isNaN(yyyy) && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && yyyy > 1000) {
      const d = new Date(yyyy, mm - 1, dd);
      if (d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd) {
        return d;
      }
    }
  }
  return null;
};

// Gets the actual range of preset dates based on today
const getPresetRange = (type) => {
  let today = new Date();
  today.setHours(0,0,0,0);
  let start = new Date(today);
  let end = new Date(today);

  switch (type) {
    case 'this_month':
      start.setDate(1);
      break;
    case 'last_30_days':
      start.setDate(today.getDate() - 30);
      break;
    case 'past_2_months':
      start.setMonth(today.getMonth() - 2);
      break;
    case 'past_3_months':
      start.setMonth(today.getMonth() - 3);
      break;
    case 'past_6_months':
      start.setMonth(today.getMonth() - 6);
      break;
    case 'past_1_year':
      start.setFullYear(today.getFullYear() - 1);
      break;
    default:
      break;
  }
  return { start, end };
};

const NewsLetterDateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activePreset, setActivePreset] = useState('custom');
  
  const [localStart, setLocalStart] = useState(parseDateFromInput(startDate));
  const [localEnd, setLocalEnd] = useState(parseDateFromInput(endDate));

  // Determine which months are showing. 
  // Normally showing the month of localEnd, and the prior month next to it? 
  // No, let's show month1 and month2 where month1 is right before month2.
  const initialBaseDate = localEnd || new Date();
  const [baseMonth, setBaseMonth] = useState(new Date(initialBaseDate.getFullYear(), initialBaseDate.getMonth(), 1));

  const popoverRef = useRef(null);
  const triggerRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({});

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click was inside popover
      if (popoverRef.current && popoverRef.current.contains(e.target)) {
        return;
      }
      // Check if click was on trigger
      if (triggerRef.current && triggerRef.current.contains(e.target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;

      // Ensure we lock the menu relatively to the document's absolute map
      setPopoverStyle({
        top: rect.bottom + scrollY + 4,
        left: rect.left + scrollX,
        position: 'absolute'
      });
    }
  }, [isOpen]);

  useEffect(() => {
    setLocalStart(parseDateFromInput(startDate));
    setLocalEnd(parseDateFromInput(endDate));
  }, [startDate, endDate]);

  const [startInputStr, setStartInputStr] = useState('');
  const [endInputStr, setEndInputStr] = useState('');

  useEffect(() => {
    setStartInputStr(localStart ? formatDateToDisplay(formatDateToInput(localStart)) : '');
  }, [localStart]);

  useEffect(() => {
    setEndInputStr(localEnd ? formatDateToDisplay(formatDateToInput(localEnd)) : '');
  }, [localEnd]);

  const handleStartInputChange = (e) => {
    const val = e.target.value;
    setStartInputStr(val);
    const d = parseDisplayToDate(val);
    if (d) {
      setLocalStart(d);
      setActivePreset('custom');
      setBaseMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    } else if (val === '') {
      setLocalStart(null);
    }
  };

  const handleEndInputChange = (e) => {
    const val = e.target.value;
    setEndInputStr(val);
    const d = parseDisplayToDate(val);
    if (d) {
      setLocalEnd(d);
      setActivePreset('custom');
    } else if (val === '') {
      setLocalEnd(null);
    }
  };

  const handleClear = () => {
    setLocalStart(null);
    setLocalEnd(null);
    setStartInputStr('');
    setEndInputStr('');
    setActivePreset('custom');
  };

  const handlePresetClick = (type) => {
    setActivePreset(type);
    if (type !== 'custom') {
      const { start, end } = getPresetRange(type);
      setLocalStart(start);
      setLocalEnd(end);
      setBaseMonth(new Date(end.getFullYear(), end.getMonth(), 1));
    }
  };

  const isDateSelected = (d) => {
    if (!localStart && !localEnd) return false;
    const time = d.getTime();
    if (localStart && localEnd) {
      return time >= localStart.getTime() && time <= localEnd.getTime();
    }
    return (localStart && time === localStart.getTime()) || (localEnd && time === localEnd.getTime());
  };

  const isDateStartOrEnd = (d) => {
    const time = d.getTime();
    return (localStart && time === localStart.getTime()) || (localEnd && time === localEnd.getTime());
  };

  const handleDayClick = (d) => {
    setActivePreset('custom');
    if (!localStart || (localStart && localEnd)) {
      setLocalStart(d);
      setLocalEnd(null);
    } else {
      if (d < localStart) {
        setLocalEnd(localStart);
        setLocalStart(d);
      } else {
        setLocalEnd(d);
      }
    }
  };

  const changeMonth = (offset) => {
    setBaseMonth(new Date(baseMonth.getFullYear(), baseMonth.getMonth() + offset, 1));
  };

  const renderCalendar = (monthOffset) => {
    const targetMonth = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + monthOffset, 1);
    const yr = targetMonth.getFullYear();
    const mo = targetMonth.getMonth();
    
    // For proper localized start of week if needed, here we use Sunday or Monday. In image it starts with Mo
    // Let's use Mo Tu We Th Fr Sa Su
    const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    
    const daysInMonth = getDaysInMonth(yr, mo);
    const firstDay = getFirstDayOfMonth(yr, mo); // 0 (Sun) to 6 (Sat)
    
    // Adjust logic to make Monday the first day
    const emptyDaysCount = firstDay === 0 ? 6 : firstDay - 1;

    let days = [];
    // Previous month filler days (just for alignment)
    for (let i = 0; i < emptyDaysCount; i++) {
        const prevMonthDays = getDaysInMonth(mo === 0 ? yr - 1 : yr, mo === 0 ? 11 : mo - 1);
        days.push({ type: 'empty', num: prevMonthDays - emptyDaysCount + i + 1 });
    }
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ type: 'day', date: new Date(yr, mo, i), num: i });
    }

    return (
      <div className="dp-calendar">
        <div className="dp-calendar-header">
          {monthOffset === -1 && (
            <button className="dp-arrow" onClick={() => changeMonth(-1)}>←</button>
          )}
          <div className="dp-month-name">{months[mo]} {yr}</div>
          {monthOffset === 0 && (
            <button className="dp-arrow" onClick={() => changeMonth(1)}>→</button>
          )}
        </div>
        <div className="dp-weekdays">
          {weekDays.map(wd => <span key={wd}>{wd}</span>)}
        </div>
        <div className="dp-days">
          {days.map((d, i) => {
            if (d.type === 'empty') return <span key={'empty'+i} className="dp-day empty">{d.num}</span>;
            
            const selected = isDateSelected(d.date);
            const edge = isDateStartOrEnd(d.date);
            
            return (
              <span 
                key={i} 
                className={`dp-day ${selected ? 'selected' : ''} ${edge ? 'edge' : ''}`}
                onClick={() => handleDayClick(d.date)}
              >
                {d.num}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const handleApply = () => {
    if (localStart && localEnd) {
        onDateChange(formatDateToInput(localStart), formatDateToInput(localEnd));
        setIsOpen(false);
    }
  };

  // The button text: "DD/MM/YYYY - DD/MM/YYYY"
  const buttonText = (startDate && endDate) 
    ? `${formatDateToDisplay(startDate)} - ${formatDateToDisplay(endDate)}`
    : 'Select Date Range';

  return (
    <div className="custom-dp-container">
      <button ref={triggerRef} className="custom-dp-trigger" onClick={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}>
        <Calendar size={16} className="custom-dp-icon" />
        <span>{buttonText}</span>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div className="custom-dp-popover" ref={popoverRef} style={{...popoverStyle, zIndex: 1050}}>
          <div className="custom-dp-sidebar">
            {PRESETS.map(p => (
              <div 
                key={p.type} 
                className={`custom-dp-preset ${activePreset === p.type ? 'active' : ''}`}
                onClick={() => handlePresetClick(p.type)}
              >
                {p.label}
              </div>
            ))}
          </div>

          <div className="custom-dp-main">
            <div className="custom-dp-calendars">
              {renderCalendar(-1)}
              {renderCalendar(0)}
            </div>
            
            <div className="custom-dp-footer">
              <div className="custom-dp-inputs">
                <span>FROM</span>
                <input 
                  type="text" 
                  value={startInputStr} 
                  onChange={handleStartInputChange}
                  className="dp-input"
                  placeholder="DD/MM/YYYY"
                />
                <span>TO</span>
                <input 
                  type="text" 
                  value={endInputStr} 
                  onChange={handleEndInputChange}
                  className="dp-input"
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="dp-clear-btn" onClick={handleClear}>Clear</button>
                <button className="dp-apply-btn" onClick={handleApply}>Apply</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewsLetterDateRangePicker;
